import {
  formatHumanName,
  type MedplumClient,
  type PatchOperation,
  type QueryTypes,
  resolveId,
} from "@medplum/core";
import type {
  Account,
  Address,
  Bundle,
  ContactPoint,
  HealthcareService,
  Media,
  Organization,
  OrganizationContact,
  Practitioner,
  PractitionerRole,
  Reference,
} from "@medplum/fhirtypes";
import { type CreateResourceOptions, FhirResource } from "@vibrant/models/fhir/FhirResource";
import { findIdentifier } from "@vibrant/models/fhir/fhir-resource-utils";
import { type PostalAddressInput, toFhirPostalAddress } from "@vibrant/models/fhir/postal-address";
import type { CalTeamClient } from "@/lib/cal/client";
import { DEFAULT_EMAIL_FOOTER_MARKDOWN } from "@/lib/email/footer-content";
import { isNotFoundError } from "@/lib/errors/is-not-found-error";
import { createLogger } from "@/lib/logger";
import { MEDMO_IDENTIFIER_SYSTEM_LOCATIONS } from "@/lib/medmo/types";
import { pruneSystemValuePairs, toFhirExtensions } from "@/lib/medplum/utils";
import {
  parseRoutingConfig,
  type RoutingConfig,
  routingConfigSchema,
} from "@/lib/messaging/routing";
import {
  type NotificationRoutingConfig,
  notificationRoutingConfigSchema,
  type ParsedNotificationRouting,
  readNotificationRouting,
} from "@/lib/notifications/routing";
import {
  buildPracticeSocialLinks,
  type PracticeSocialLinks,
  parsePracticeSocialLinks,
  practiceSocialLinksSchema,
} from "@/lib/practice/social-links";
import {
  STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS,
  STRIPE_IDENTIFIER_SYSTEM_CUSTOMERS,
} from "@/lib/stripe/constants";
import { getVibrantSuperOrgId } from "@/lib/utils";
import { normalizeUSPhoneNumber, toE164PhoneNumber } from "@/lib/utils/phone/phone-utils";
import { MedplumFile } from "../files/MedplumFile";
import { type InviteStatus, MemberAccount } from "../member/accounts/MemberAccount";
import { Purchase } from "../member/accounts/Purchase";
import type { Member } from "../member/Member";
import { Provider } from "../provider/Provider";
import { ProviderRole, type ProviderRoleInviteStatus } from "../provider/ProviderRole";
import type { RoleType } from "../provider/RoleDefinition";
import {
  type ExtensionPatchRequest,
  findExtension,
  getExtensionPatchOperations,
  getIdentifierPatchOperations,
  type IdentifierPatchRequest,
} from "../utils";
import { getService, Service } from "./services/Service";

const logger = createLogger("Practice");

export const VIBRANT_EXTENSION_URL_PRACTICE_TIMEZONE = "vibrant.practices.timezone";
export const VIBRANT_EXTENSION_URL_PRACTICE_PATIENT_AGREEMENT_TEMPLATE_ID =
  "vibrant.practices.patientAgreementTemplateId";
export const VIBRANT_EXTENSION_URL_PRACTICE_LOGO = "vibrant.practices.logo";
export const VIBRANT_EXTENSION_URL_PRACTICE_LOGO_SUPABASE_URL =
  "vibrant.practices.logo.supabaseUrl";
export const VIBRANT_EXTENSION_URL_INVITE_WITHOUT_PAYMENT = "vibrant.invite.withoutPayment";
export const VIBRANT_EXTENSION_URL_PRACTICE_SKIP_PATIENT_AGREEMENTS =
  "vibrant.practice.skipPatientAgreements";
export const VIBRANT_EXTENSION_URL_PRACTICE_SKIP_PAYMENT_SETUP =
  "vibrant.practice.skipPaymentSetup";
export const VIBRANT_EXTENSION_URL_PRACTICE_CHECK_OFF_TEAM_INVITES =
  "vibrant.practice.checkOffTeamInvites";
export const VIBRANT_EXTENSION_URL_PRACTICE_SEND_PATIENT_REACTIVATION_EMAILS =
  "vibrant.practice.sendPatientReactivationEmails";
export const VIBRANT_EXTENSION_URL_PRACTICE_SEND_PATIENT_EMAILS =
  "vibrant.practice.sendPatientEmails";

const VITAL_IDENTIFIER_SYSTEM_TEAMS = "vital.teams";
const VITAL_IDENTIFIER_SYSTEM_WEBHOOK_IDS = "vital.webhookIds";

const PHOTON_ORGS = "photon.orgs";
const PHOTON_FRONTEND_CLIENT_IDS = "photon.frontend.clientIds";
const PHOTON_BACKEND_CLIENT_IDS = "photon.backend.clientIds";

const CAL_IDENTIFIER_SYSTEM_TEAMS = "cal.teams";

export interface CreatePracticeRequest {
  name: string;
  slug: string;
  adminName: string;
  adminEmail: string;
  adminNpi: string;
  phone?: string;
  phoneExt?: string;
  fax?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  zipCode: string;
  stateTwoLetter: string;
  purchaseFaxNumber?: boolean;
  labPartnerInterfaces?: Record<string, boolean>;
  taxId?: string;
}

type UpdatePracticeData = Omit<
  Partial<CreatePracticeRequest>,
  "purchaseFaxNumber" | "slug" | "taxId" | "labPartnerInterfaces" | "adminNpi"
>;

export const StripePaymentSetupStatus = {
  Pending: "pending",
  Ready: "ready",
  Failed: "failed",
} as const;

export type StripePaymentSetupStatus =
  (typeof StripePaymentSetupStatus)[keyof typeof StripePaymentSetupStatus];

export interface PracticeSearchQuery {
  slug?: string;
  stripeAccountId?: string;
  medmoLocationId?: number;
  faxNumber?: string;
}

/**
 * `patchPractice` handles ordinary (non-secret) Organization fields only — it's called directly
 * from client components, so it must never touch `PracticeSecretStore` (see
 * `models/practice/practice-secrets-patch.ts`). `vitalApiKey`, `photonBackendClientSecret`, and
 * `webhookSigningSecret` are deliberately absent here; write those via `patchPracticeSecrets`
 * (server-only callers) or the `updatePracticeSecrets` server action (client-triggered flows).
 */
export interface PracticePatchQuery {
  name?: string;
  adminContact?: {
    adminName: string;
    adminEmail: string;
  };
  patientAgreementTemplateId?: string;
  skipPatientAgreements?: boolean;
  stripeAccountId?: string;
  stripePaymentSetupStatus?: StripePaymentSetupStatus | null;
  stripeCustomerId?: string;
  medmoLocationId?: number;
  address?: PostalAddressInput;
  phone?: string;
  phoneExt?: string;
  fax?: string;
  vitalTeamId?: string;
  vitalWebhookId?: string;
  photonOrgId?: string;
  photonFrontendClientId?: string;
  photonBackendClientId?: string;
  skipPaymentSetup?: boolean;
  skipTeamInvites?: boolean;
  sendPatientReactivationEmails?: boolean;
  /** The practice's own social profiles, normalized on write. */
  socialLinks?: Partial<Record<"instagram" | "linkedin" | "twitter", string | undefined>>;
  /** Editable markdown for the email footer. Blank restores the default on the next read. */
  emailFooterMarkdown?: string;
  /** Super-admin kill switch for product email to patients. */
  sendPatientEmails?: boolean;
  timezone?: string;
  labPartnerInterfaces?: Record<string, boolean>;
  taxId?: string | null;
  calTeamId?: string;
  /** Organization-wide GTM container id; pass null to clear. */
  gtmContainerId?: string | null;
}

export type SerializedPractice = Organization;

interface AddServiceForMemberParams {
  service: Service;
  member: Member;
  inviteStatus?: InviteStatus;
}

export class Practice extends FhirResource<Organization> {
  static readonly ResourceType = "Organization" as const;

  static readonly Identifier = {
    HintPracticeId: "vibrant.practice.hintPracticeId",
    HintIntegrationId: "vibrant.practice.hintIntegrationId",
    /**
     * We use this identifier to store the fax number of the practice because the `telecom` prop is
     * not indexed by Medplum, so we can't search for it.
     */
    Fax: "vibrant.practice.fax",
    TaxId: "vibrant.practice.taxId",
    CalTeamId: CAL_IDENTIFIER_SYSTEM_TEAMS,
  } as const;

  static readonly Extension = {
    PatientAgreementTemplateId: VIBRANT_EXTENSION_URL_PRACTICE_PATIENT_AGREEMENT_TEMPLATE_ID,
    /** ISO 8601 instant the current `HintAccessToken` expires, if Hint reported one. */
    HintTokenExpiresAt: "vibrant.practice.hintTokenExpiresAt",
    SkipPatientAgreements: VIBRANT_EXTENSION_URL_PRACTICE_SKIP_PATIENT_AGREEMENTS,
    /**
     * ISO 8601 instant (valueString) the practice got its first agreement template. Written once, by `patchPractice`, on
     * the empty -> set change. A practice with a template but no value never gets the backfill prompt.
     */
    PatientAgreementTemplateSetAt: "vibrant.practice.patient-agreement-template-set-at",
    /**
     * Template id whose agreement backfill prompt a provider already answered (sent or dismissed).
     * Matches `PatientAgreementTemplateId` once the practice decided; see
     * `data/member/patient-agreement-backfill.ts`.
     */
    PatientAgreementBackfillResolvedTemplateId:
      "vibrant.practice.patient-agreement-backfill-resolved-template-id",
    SkipPaymentSetup: VIBRANT_EXTENSION_URL_PRACTICE_SKIP_PAYMENT_SETUP,
    CheckOffTeamInvites: VIBRANT_EXTENSION_URL_PRACTICE_CHECK_OFF_TEAM_INVITES,
    SendPatientReactivationEmails: VIBRANT_EXTENSION_URL_PRACTICE_SEND_PATIENT_REACTIVATION_EMAILS,
    /**
     * Practice-wide kill switch for product email to patients. Super admin only. Absent means send.
     * Auth and security mail ignores it - see lib/email/patient-email-policy.ts.
     */
    SendPatientEmails: VIBRANT_EXTENSION_URL_PRACTICE_SEND_PATIENT_EMAILS,
    Logo: VIBRANT_EXTENSION_URL_PRACTICE_LOGO,
    LogoSupabaseUrl: VIBRANT_EXTENSION_URL_PRACTICE_LOGO_SUPABASE_URL,
    Timezone: VIBRANT_EXTENSION_URL_PRACTICE_TIMEZONE,
    StripePaymentSetupStatus: "vibrant.practice.stripePaymentSetupStatus",
    /** JSON blob of clinician-messaging routing config (see lib/messaging/routing). Off by default. */
    MessagingRoutingConfig: "vibrant.practice.messaging-routing-config",
    /** JSON blob of in-app notification routing (see lib/notifications/routing). Absent = defaults. */
    NotificationRoutingConfig: "vibrant.practice.notification-routing-config",
    /** Organization-wide Google Tag Manager container; a widget's own GTM overrides it. */
    GtmContainerId: "vibrant.practice.gtm-container-id",
    /** JSON blob of the practice's own social profiles (see lib/practice/social-links). */
    SocialLinks: "vibrant.practice.social-links",
    /** Editable markdown for the email footer (see lib/email/footer-content). */
    EmailFooterMarkdown: "vibrant.practice.email-footer-markdown",
  } as const;

  //------------------------------------------------------------------------------------------------
  //                                        GETTERS
  //------------------------------------------------------------------------------------------------

  get id(): string {
    return this.resource.id!;
  }

  /**
   * Clinician-messaging routing config (Feature 4). Falls back to the off-by-default config when a
   * practice has never configured routing, so a solo clinic transparently gets the simple behavior.
   */
  get messagingRoutingConfig(): RoutingConfig {
    return parseRoutingConfig(
      this.getExtension(Practice.Extension.MessagingRoutingConfig)?.valueString,
    );
  }

  /** Persists the routing config as a validated JSON blob, merging by extension URL. */
  async updateMessagingRoutingConfig(
    medplum: MedplumClient,
    config: RoutingConfig,
  ): Promise<Practice> {
    const updated = await Practice.patchResource(medplum, {
      id: this.id,
      patch: {
        extension: toFhirExtensions([
          {
            url: Practice.Extension.MessagingRoutingConfig,
            valueString: JSON.stringify(routingConfigSchema.parse(config)),
          },
        ]),
      },
    });
    return new Practice(updated);
  }

  /**
   * The practice's email footer, as markdown it can edit.
   *
   * Falls back to the traditional default, so a practice that has never opened the footer editor
   * still sends a correct footer.
   */
  get emailFooterMarkdown(): string {
    const stored = this.getExtension(Practice.Extension.EmailFooterMarkdown)?.valueString;
    return stored?.trim() ? stored : DEFAULT_EMAIL_FOOTER_MARKDOWN;
  }

  /**
   * The practice's own social profiles. Empty when the practice has never entered any, which drops
   * the social row from its email footer.
   */
  get socialLinks(): PracticeSocialLinks {
    return parsePracticeSocialLinks(this.getExtension(Practice.Extension.SocialLinks)?.valueString);
  }

  /** Who receives each automated in-app notification type. Defaults when never saved. */
  get notificationRoutingConfig(): NotificationRoutingConfig {
    return this.notificationRouting.config;
  }

  /** The routing plus the saved routes that no longer parse and fell back to their default. */
  get notificationRouting(): ParsedNotificationRouting {
    return readNotificationRouting(
      this.getExtension(Practice.Extension.NotificationRoutingConfig)?.valueString,
    );
  }

  /** Persists the notification routing as a validated JSON blob, merging by extension URL. */
  async updateNotificationRoutingConfig(
    medplum: MedplumClient,
    config: NotificationRoutingConfig,
  ): Promise<Practice> {
    const updated = await Practice.patchResource(medplum, {
      id: this.id,
      patch: {
        extension: toFhirExtensions([
          {
            url: Practice.Extension.NotificationRoutingConfig,
            valueString: JSON.stringify(notificationRoutingConfigSchema.parse(config)),
          },
        ]),
      },
    });
    return new Practice(updated);
  }

  get name(): string {
    const name = this.resource.name;
    if (!name) {
      throw new Error("Practice name not found");
    }
    return name;
  }

  get display(): string {
    return this.name;
  }

  get email(): string | undefined {
    const email = this.adminContact.telecom?.find((com) => com.system === "email")?.value;
    return email;
  }

  get address(): Address | undefined {
    // we assume the practice HQ is the first address
    const address = this.resource.address?.[0];
    return address;
  }

  get addressLine1(): string | undefined {
    return this.address?.line?.[0];
  }

  get addressLine2(): string | undefined {
    return this.address?.line?.[1];
  }

  get city(): string | undefined {
    return this.address?.city;
  }

  get state(): string | undefined {
    return this.address?.state;
  }

  get zipCode(): string | undefined {
    return this.address?.postalCode;
  }

  get country(): string {
    return "US"; // hardcoded for now
  }

  /**
   * Practice timezone (IANA ID), if available. Example: "America/Chicago".
   */
  get timezone(): string | undefined {
    const extension = findExtension(this.resource, Practice.Extension.Timezone);
    return extension?.valueString;
  }

  /**
   * Organization-wide Google Tag Manager container id, if configured. Acts as the default for
   * embed booker widgets — a widget's own GTM container takes precedence over this.
   */
  get gtmContainerId(): string | undefined {
    return findExtension(this.resource, Practice.Extension.GtmContainerId)?.valueString;
  }

  /**
   * Check if a specific lab partner is in interface mode (practice brings their own account).
   * Defaults to false if not specified.
   *
   * @param partnerSlug - The lab partner slug (e.g., "quest", "labcorp")
   * @returns true if interface mode, false if subaccount mode
   */
  isUsingLabInterface(partnerSlug: string): boolean {
    const extension = findExtension(
      this.resource,
      `vibrant.practice.lab-partner.${partnerSlug}.is-interface`,
    );
    return extension?.valueBoolean ?? false;
  }

  /**
   * Junction lab account id (uuid from `GET /v3/lab_test/lab_account`) that interface orders for
   * this lab partner are placed on. Junction needs it when the practice's team has more than one
   * orderable account for the lab - without it, Junction falls back to its platform account.
   *
   * @param partnerSlug - The lab partner slug (e.g., "quest", "labcorp")
   * @returns the lab account id, or undefined if none is configured
   */
  getLabAccountId(partnerSlug: string): string | undefined {
    const extension = findExtension(
      this.resource,
      `vibrant.practice.lab-partner.${partnerSlug}.lab-account-id`,
    );
    return extension?.valueString;
  }

  async admin(medplum: MedplumClient): Promise<Provider> {
    const provider = await Provider.findInPracticeByEmail(medplum, {
      practiceRef: this.referenceString,
      email: this.adminEmail,
    });

    if (!provider) {
      throw new Error("Admin provider not found");
    }
    return provider;
  }

  get optionalAdminContact(): OrganizationContact | undefined {
    return this.resource.contact?.find((contact) =>
      contact.purpose?.coding?.find((coding) => coding.code === "admin"),
    );
  }

  get adminContact(): OrganizationContact {
    const adminContact = this.optionalAdminContact;
    if (!adminContact) {
      throw new Error("Admin contact not found");
    }
    return adminContact;
  }

  get optionalAdminName(): string | undefined {
    const adminHumanName = this.optionalAdminContact?.name;
    return adminHumanName ? formatHumanName(adminHumanName) : undefined;
  }

  get adminName(): string {
    const adminName = this.optionalAdminName;
    if (adminName === undefined) {
      throw new Error("Admin name not found");
    }
    return adminName;
  }

  get optionalAdminEmail(): string | undefined {
    return this.optionalAdminContact?.telecom?.find((com) => com.system === "email")?.value;
  }

  get adminEmail(): string {
    const email = this.optionalAdminEmail;
    if (!email) {
      throw new Error("Admin email not found");
    }
    return email;
  }

  get adminNpi(): string | undefined {
    const extension = findExtension(this.resource, Provider.Identifier.NPI);
    return extension?.valueString;
  }

  get phone(): string | undefined {
    const phoneString = this.resource.telecom?.find((com) => com.system === "phone")?.value;
    return phoneString ? phoneString.split(",")[0] : undefined;
  }

  get phoneExt(): string | undefined {
    const phoneString = this.resource.telecom?.find((com) => com.system === "phone")?.value;
    return phoneString ? phoneString.split(",")[1] : undefined;
  }

  get fax(): string | undefined {
    return this.resource.telecom?.find((com) => com.system === "fax")?.value;
  }

  /**
   * The fax number to show a provider: the contact fax, else the fax identifier that inbound faxes
   * route on. Some practices (e.g. the local dev seed) carry only the identifier.
   */
  get displayFax(): string | undefined {
    return this.fax ?? findIdentifier(this.resource, Practice.Identifier.Fax)?.value;
  }

  get taxId(): string | undefined {
    const identifier = findIdentifier(this.resource, Practice.Identifier.TaxId);
    return identifier?.value;
  }

  get slug(): string {
    const identifier = findIdentifier(this.resource, "slug");
    return identifier?.value ?? "";
  }

  get stripeAccountId(): string | undefined {
    const identifier = findIdentifier(this.resource, STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS);
    return identifier?.value;
  }

  get stripeCustomerId(): string | undefined {
    const identifier = findIdentifier(this.resource, STRIPE_IDENTIFIER_SYSTEM_CUSTOMERS);
    return identifier?.value;
  }

  get canManageServices(): boolean {
    return Boolean(this.stripeAccountId);
  }

  get canCollectPayments(): boolean {
    if (!this.stripeAccountId) {
      return false;
    }

    return this.stripePaymentSetupStatus === StripePaymentSetupStatus.Ready;
  }

  get stripePaymentSetupStatus(): StripePaymentSetupStatus | undefined {
    const extension = findExtension(this.resource, Practice.Extension.StripePaymentSetupStatus);
    const status = extension?.valueCode ?? extension?.valueString;
    if (!status) {
      return undefined;
    }
    if (Object.values(StripePaymentSetupStatus).includes(status as StripePaymentSetupStatus)) {
      return status as StripePaymentSetupStatus;
    }
    return undefined;
  }

  get medmoLocationId(): number | undefined {
    const identifier = findIdentifier(this.resource, MEDMO_IDENTIFIER_SYSTEM_LOCATIONS);
    return identifier?.value ? parseInt(identifier.value, 10) : undefined;
  }

  get patientAgreementTemplateId(): string | undefined {
    const extension = findExtension(
      this.resource,
      VIBRANT_EXTENSION_URL_PRACTICE_PATIENT_AGREEMENT_TEMPLATE_ID,
    );
    return extension?.valueString;
  }

  /** ISO 8601 instant the first agreement template was set, when recorded. */
  get patientAgreementTemplateSetAt(): string | undefined {
    return findExtension(this.resource, Practice.Extension.PatientAgreementTemplateSetAt)
      ?.valueString;
  }

  /** Template id whose backfill prompt a provider already sent or dismissed. */
  get patientAgreementBackfillResolvedTemplateId(): string | undefined {
    return findExtension(
      this.resource,
      Practice.Extension.PatientAgreementBackfillResolvedTemplateId,
    )?.valueString;
  }

  get hasSkippedPatientAgreements(): boolean {
    const extension = findExtension(this.resource, Practice.Extension.SkipPatientAgreements);
    return extension?.valueBoolean ?? false;
  }

  get hasSkippedPaymentSetup(): boolean {
    const extension = findExtension(this.resource, Practice.Extension.SkipPaymentSetup);
    return extension?.valueBoolean ?? false;
  }

  get hasCheckedOffTeamInvites(): boolean {
    const extension = findExtension(this.resource, Practice.Extension.CheckOffTeamInvites);
    return extension?.valueBoolean ?? false;
  }

  get shouldSendPatientReactivationEmails(): boolean {
    const extension = findExtension(
      this.resource,
      Practice.Extension.SendPatientReactivationEmails,
    );
    return extension?.valueBoolean ?? true;
  }

  /**
   * False when a super admin turned off product email to this practice's patients. Defaults true so
   * a practice that never had the flag set keeps sending. Read it through
   * `isPatientEmailSuppressed` rather than branching on it directly - that helper owns the
   * auth-mail carve-out and the suppression telemetry.
   */
  get shouldSendPatientEmails(): boolean {
    const extension = findExtension(this.resource, Practice.Extension.SendPatientEmails);
    return extension?.valueBoolean ?? true;
  }

  get logo(): Reference<Media> | undefined {
    const extension = findExtension(this.resource, Practice.Extension.Logo);
    return extension?.valueString ? { reference: extension.valueString } : undefined;
  }

  get logoCdnUrl(): string | undefined {
    const extension = findExtension(this.resource, Practice.Extension.LogoSupabaseUrl);
    return extension?.valueString;
  }

  get vitalTeamId(): string | undefined {
    const identifier = findIdentifier(this.resource, VITAL_IDENTIFIER_SYSTEM_TEAMS);
    return identifier?.value;
  }

  get vitalWebhookId(): string | undefined {
    const identifier = findIdentifier(this.resource, VITAL_IDENTIFIER_SYSTEM_WEBHOOK_IDS);
    return identifier?.value;
  }

  get photonOrgId(): string | undefined {
    const identifier = findIdentifier(this.resource, PHOTON_ORGS);
    return identifier?.value;
  }

  get photonFrontendClientId(): string | undefined {
    const identifier = findIdentifier(this.resource, PHOTON_FRONTEND_CLIENT_IDS);
    return identifier?.value;
  }

  get photonBackendClientId(): string | undefined {
    const identifier = findIdentifier(this.resource, PHOTON_BACKEND_CLIENT_IDS);
    return identifier?.value;
  }

  get hintPracticeId(): string | undefined {
    const identifier = findIdentifier(this.resource, Practice.Identifier.HintPracticeId);
    return identifier?.value;
  }

  get hintIntegrationId(): string | undefined {
    const identifier = findIdentifier(this.resource, Practice.Identifier.HintIntegrationId);
    return identifier?.value;
  }

  /** ISO 8601 instant the current `hintAccessToken` expires, if known. */
  get hintTokenExpiresAt(): string | undefined {
    const extension = findExtension(this.resource, Practice.Extension.HintTokenExpiresAt);
    return extension?.valueString;
  }

  /**
   * Get the Cal.com team ID for this practice (Calendar V2)
   */
  get calTeamId(): string | undefined {
    const identifier = findIdentifier(this.resource, Practice.Identifier.CalTeamId);
    return identifier?.value;
  }

  //------------------------------------------------------------------------------------------------
  //                                    INSTANCE METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Get all active (non-archived) services for the practice
   *
   * @param medplum - The Medplum client
   * @param options - Optional pagination parameters
   * @returns The services sorted by last updated date (newest first)
   */
  async getActiveServices(
    medplum: MedplumClient,
    options?: { limit?: number; offset?: number },
  ): Promise<Service[]> {
    const baseQuery: QueryTypes = {
      organization: `Organization/${this.id}`,
      active: "true",
      _sort: "-_lastUpdated",
    };

    // If caller provided explicit pagination options, perform a single paged search.
    // NOTE: Medplum defaults `_count` to 20 when omitted, so callers that want "all"
    // results should omit limit/offset and rely on the paginated fetch below.
    if (options?.limit !== undefined || options?.offset !== undefined) {
      const healthcareServices = await medplum.searchResources("HealthcareService", {
        ...baseQuery,
        _count: options?.limit,
        _offset: options?.offset,
      });
      return healthcareServices.map((service) => new Service(service));
    }

    // Fetch all services, following Bundle.link[relation="next"] until exhausted.
    // This prevents the common "only first 20 results" bug when `_count` is omitted.
    const PAGE_SIZE = 1000; // Medplum max page size
    const MAX_PAGES = 50; // safety guard against infinite pagination loops

    const all: Service[] = [];
    let bundle: Bundle = await medplum.search("HealthcareService", {
      ...baseQuery,
      _count: PAGE_SIZE,
    });

    for (let page = 0; page < MAX_PAGES; page += 1) {
      const healthcareServices = (bundle.entry ?? [])
        .map((entry) => entry.resource)
        .filter(
          (resource): resource is HealthcareService =>
            !!resource && resource.resourceType === "HealthcareService",
        );

      all.push(...healthcareServices.map((service) => new Service(service)));

      const nextLink = bundle.link?.find((link) => link.relation === "next");
      if (!nextLink?.url) {
        return all;
      }

      if (healthcareServices.length === 0) {
        logger.error("Active services pagination returned no results but still had next link", {
          practiceId: this.id,
          pageSize: PAGE_SIZE,
          nextUrl: nextLink.url,
        });
        throw new Error("Failed to paginate active services");
      }

      bundle = (await medplum.get(nextLink.url)) as Bundle;
    }

    logger.error("Exceeded max pages when fetching active services", {
      practiceId: this.id,
      pageSize: PAGE_SIZE,
      maxPages: MAX_PAGES,
      fetchedCount: all.length,
    });
    throw new Error("Too many active services to fetch");
  }

  /**
   * Get the total count of active services for the practice
   *
   * @param medplum - The Medplum client
   * @returns The total count of active services
   */
  async getActiveServicesCount(medplum: MedplumClient): Promise<number> {
    const bundle = await medplum.search("HealthcareService", {
      organization: `Organization/${this.id}`,
      active: "true",
      _total: "accurate",
      _count: 0,
    });
    return bundle.total || 0;
  }

  /**
   * Adds a member synced from data migration with an active account
   *
   * @param medplum - The Medplum client
   * @param member - The member to add
   * @returns The account
   */
  async addDataMigrationMember(medplum: MedplumClient, member: Member): Promise<MemberAccount> {
    const memberAccount = await MemberAccount.upsert(medplum, {
      member: { reference: `Patient/${member.id}` },
      practice: { reference: `Organization/${this.id}` },
      status: MemberAccount.Status.Active,
      syncedFromDataMigration: true,
      inviteStatus: MemberAccount.InviteStatus.Pending,
    });
    return memberAccount;
  }

  /**
   * Adds a member synced from Stripe to a practice with an active account
   *
   * @param medplum - The Medplum client
   * @param member - The member to add
   * @returns The account
   */
  async addStripeSyncedMember(medplum: MedplumClient, member: Member): Promise<MemberAccount> {
    // ENG-5280: this activates the account without calling MemberAccount.markInviteAccepted,
    // so `inviteStatus` stays stale ("Invite sent") for an account that is actually active.
    // Log-only for now — see the Goalkeeper recommendation to reconcile inviteStatus here too.
    logger.warn("Activating account via Stripe sync without advancing inviteStatus", {
      memberId: member.id ?? null,
      practiceId: this.id ?? null,
    });
    const memberAccount = await MemberAccount.upsert(medplum, {
      member: { reference: `Patient/${member.id}` },
      practice: { reference: `Organization/${this.id}` },
      status: MemberAccount.Status.Active,
      syncedFromStripe: true,
    });
    return memberAccount;
  }

  async activateMember(medplum: MedplumClient, member: Member): Promise<MemberAccount> {
    const memberRef = { reference: `Patient/${member.id}` };
    const practiceRef = { reference: `Organization/${this.id}` };
    const activeAccount = await MemberAccount.searchActiveForMemberPractice(medplum, {
      member: memberRef,
      practice: practiceRef,
    });

    if (activeAccount) {
      return activeAccount;
    }

    // ENG-5283: same gap as addStripeSyncedMember above — activates without advancing
    // inviteStatus, and without the agreement-send/questionnaire-resolution bundle that
    // checkout-session-completed.ts normally runs.
    logger.warn("Activating member without advancing inviteStatus", {
      memberId: member.id ?? null,
      practiceId: this.id ?? null,
    });
    return MemberAccount.upsert(medplum, {
      member: memberRef,
      practice: practiceRef,
      status: MemberAccount.Status.Active,
    });
  }

  async markMemberAsInactive(medplum: MedplumClient, member: Member): Promise<MemberAccount> {
    const memberRef = { reference: `Patient/${member.id}` };
    const practiceRef = { reference: `Organization/${this.id}` };
    const activeAccount = await MemberAccount.searchActiveForMemberPractice(medplum, {
      member: memberRef,
      practice: practiceRef,
    });

    if (activeAccount) {
      const accountRef = activeAccount.referenceObject;
      const purchases = accountRef ? await Purchase.search(medplum, { account: accountRef }) : [];
      const billablePurchases = purchases.filter(
        (purchase) => purchase.status === Purchase.Status.Unpaid,
      );

      if (billablePurchases.length > 0) {
        logger.warn("Marking member account inactive with billable purchases", {
          memberAccountId: activeAccount.id,
          memberId: member.id,
          practiceId: this.id,
          purchaseIds: billablePurchases.map((purchase) => purchase.id),
        });
      }

      // Hint owns this transition — `markMemberAsInactive` is reached only from the Hint
      // `patient.inactive` webhook — so Hint is allowed to reverse it when the patient comes back.
      return activeAccount.markInactive(medplum, {
        source: MemberAccount.DeactivationSource.Hint,
      });
    }

    // No *active* account, so `upsert` may be patching one that is already inactive — including
    // one a provider deactivated. Writing the Hint marker over that provider marker would hand
    // the member back to the membership sync, which then reactivates someone a clinician removed
    // on purpose. Leave that account exactly as it is (ENG-5273).
    const existingAccount = await MemberAccount.searchOne(medplum, {
      member: memberRef,
      practice: practiceRef,
    });

    // a chart removal marker is protected the same way
    if (existingAccount?.isSyncProtected) {
      logger.info("Hint reported a sync-protected member inactive; leaving the marker", {
        memberAccountId: existingAccount.id,
        deactivationSource: existingAccount.deactivationSource ?? null,
        memberId: member.id,
        practiceId: this.id,
      });
      return existingAccount;
    }

    return MemberAccount.upsert(medplum, {
      member: memberRef,
      practice: practiceRef,
      status: MemberAccount.Status.Inactive,
      deactivationSource: MemberAccount.DeactivationSource.Hint,
    });
  }

  /**
   * Add a member with service choices to the practice.
   *
   * @param medplum - The Medplum client.
   * @param member - The member to add.
   * @param serviceChoiceIds - Optional array of service IDs that the member is choosing from.
   * @returns The account for the member.
   */
  async addMemberWithServiceChoices(
    medplum: MedplumClient,
    member: Member,
    serviceChoiceIds?: string[],
  ): Promise<MemberAccount> {
    const memberRef = { reference: `Patient/${member.id}` };
    const practiceRef = { reference: `Organization/${this.id}` };
    const existingActiveAccount = await MemberAccount.searchActiveForMemberPractice(medplum, {
      member: memberRef,
      practice: practiceRef,
    });

    if (existingActiveAccount?.isActive === true && !serviceChoiceIds?.length) {
      return existingActiveAccount;
    }

    let memberAccount: MemberAccount;

    if (existingActiveAccount?.isActive === true && serviceChoiceIds?.length) {
      logger.info("Repairing existing active member account with requested service choices", {
        memberAccountId: existingActiveAccount.id,
        memberId: member.id,
        practiceId: this.id,
        serviceIds: serviceChoiceIds,
      });

      memberAccount = await MemberAccount.update(medplum, existingActiveAccount.id, {
        member: memberRef,
        practice: practiceRef,
        serviceChoiceIds,
        status: MemberAccount.Status.Active,
        inviteWithoutPayment: false,
      });
    } else {
      const existingAccount = await MemberAccount.searchOne(medplum, {
        member: memberRef,
        practice: practiceRef,
      });

      const shouldSetInviteStatus = !existingAccount;
      memberAccount = await MemberAccount.upsert(medplum, {
        member: memberRef,
        practice: practiceRef,
        serviceChoiceIds,
        status: MemberAccount.Status.Active,
        inviteWithoutPayment: serviceChoiceIds?.length ? false : undefined,
        inviteStatus: shouldSetInviteStatus ? MemberAccount.InviteStatus.Pending : undefined,
      });
    }

    if (serviceChoiceIds && serviceChoiceIds.length > 0) {
      await ensurePendingServicePurchases({
        medplum,
        memberAccount,
        practiceId: this.id,
        serviceIds: serviceChoiceIds,
      });
    }

    return memberAccount;
  }

  /**
   * Adds a member to a practice without requiring service payment during invite.
   *
   * @param medplum - The Medplum client
   * @param member - The member to add
   * @returns The account
   */
  async addMemberWithoutServices(medplum: MedplumClient, member: Member): Promise<MemberAccount> {
    const memberRef = { reference: `Patient/${member.id}` };
    const practiceRef = { reference: `Organization/${this.id}` };
    const existingActiveAccount = await MemberAccount.searchActiveForMemberPractice(medplum, {
      member: memberRef,
      practice: practiceRef,
    });

    if (existingActiveAccount?.isActive === true) {
      logger.info("Repairing existing active member account for no-payment invite", {
        memberAccountId: existingActiveAccount.id,
        memberId: member.id,
        practiceId: this.id,
      });

      const memberAccount = await MemberAccount.update(medplum, existingActiveAccount.id, {
        member: memberRef,
        practice: practiceRef,
        status: MemberAccount.Status.Active,
        serviceChoiceIds: null,
        serviceId: null,
        inviteWithoutPayment: true,
      });

      await markUnpaidServicePurchasesNotBillable({
        medplum,
        memberAccount,
        practiceId: this.id,
      });

      return memberAccount;
    }

    const existingAccount = await MemberAccount.searchOne(medplum, {
      member: memberRef,
      practice: practiceRef,
    });
    const shouldSetInviteStatus = !existingAccount;
    const memberAccount = await MemberAccount.upsert(medplum, {
      member: memberRef,
      practice: practiceRef,
      status: MemberAccount.Status.Active,
      inviteWithoutPayment: true,
      inviteStatus: shouldSetInviteStatus ? MemberAccount.InviteStatus.Pending : undefined,
    });

    // An account that existed but wasn't active can still carry unpaid service charges from an
    // earlier with-payment invite. The extension alone hides them — `derivePlanStepState`
    // short-circuits on it before reading purchases — so the member reads "Active" while still
    // owing money. Void them here, same as the active-account branch above. Skipped when no
    // prior account existed: a brand-new invite has nothing to void.
    if (existingAccount) {
      await markUnpaidServicePurchasesNotBillable({
        medplum,
        memberAccount,
        practiceId: this.id,
      });
    }

    return memberAccount;
  }

  /**
   * Add a service for a member
   *
   * @param medplum - The Medplum client
   * @param service - The service to add
   * @param member - The member to add the service to
   * @param options - Optional overrides such as invite status
   * @returns The account
   */
  async addServiceForMember(
    medplum: MedplumClient,
    { service, member, inviteStatus }: AddServiceForMemberParams,
  ): Promise<MemberAccount> {
    const memberAccount = await MemberAccount.addService(medplum, {
      member: { reference: `Patient/${member.id}` },
      practice: { reference: `Organization/${this.id}` },
      serviceId: service.id,
      inviteStatus,
    });
    return memberAccount;
  }

  /**
   * Add a provider to the practice
   *
   * @param medplum - The Medplum client
   * @param provider - The provider to add
   * @param options - The options for the provider
   * @returns The practitioner role
   */
  async addProvider(
    medplum: MedplumClient,
    provider: Provider,
    options?: { isPracticeAdmin: boolean; role: RoleType; inviteStatus?: ProviderRoleInviteStatus },
  ): Promise<ProviderRole> {
    const providerRole = await ProviderRole.searchOne(medplum, {
      practitioner: provider.referenceObject,
    });

    // Prevent the provider from being added to more than one practice.
    if (providerRole && providerRole.organization?.reference !== this.referenceString) {
      throw new Error("Provider is already associated with another practice");
    }

    if (!providerRole?.id) {
      return await ProviderRole.create(
        medplum,
        {
          practitioner: provider.referenceObject,
          organization: this.referenceObject,
          role: options?.role ?? "prescribing-provider",
          active: true,
          isPracticeAdmin: options?.isPracticeAdmin ?? false,
          inviteStatus: options?.inviteStatus,
        },
        { practiceOrganization: this.referenceString },
      );
    }

    // TODO: Add the organization/practice as a compartment to the practitioner.
    return await ProviderRole.update(medplum, providerRole.id, {
      isPracticeAdmin: options?.isPracticeAdmin ?? false,
      role: options?.role,
      active: true,
      inviteStatus: options?.inviteStatus,
    });
  }

  /**
   * Upload and set a logo for the practice
   *
   * @param medplum - The Medplum client
   * @param file - The logo file to upload
   * @param cdnLogoUrl - The URL of the logo in the CDN
   * @returns The updated practice
   */
  async uploadLogo(medplum: MedplumClient, file: File, cdnLogoUrl: string): Promise<Practice> {
    // First create a Binary resource
    const medplumFile = await MedplumFile.upload(medplum, {
      data: new Uint8Array(await file.arrayBuffer()),
      filename: file.name,
      contentType: file.type,
    });

    // Then create a Media resource referencing the Binary
    const media = await medplum.createResource<Media>({
      resourceType: "Media",
      status: "completed",
      content: {
        contentType: file.type,
        url: `Binary/${medplumFile.id}`,
        title: file.name,
      },
    });

    // Finally update the practice with the Media reference
    const extensionPatchRequests: ExtensionPatchRequest[] = [
      {
        resource: this.resource,
        url: Practice.Extension.Logo,
        value: `Media/${media.id}`,
      },
      {
        resource: this.resource,
        url: Practice.Extension.LogoSupabaseUrl,
        value: cdnLogoUrl,
      },
    ];

    const patchedOrganization = await medplum.patchResource(
      "Organization",
      this.id,
      getExtensionPatchOperations(this.resource, extensionPatchRequests),
    );

    return new Practice(patchedOrganization);
  }

  async removeStripeAccountId(medplum: MedplumClient): Promise<Practice> {
    const identifier = this.resource.identifier;

    if (!identifier) {
      return this;
    }

    const stripeAccountIndex = identifier.findIndex(
      (id) => id.system === STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS,
    );

    if (stripeAccountIndex === -1) {
      // Stripe account identifier not found, no changes needed
      return this;
    }

    // Create a new identifier array excluding the Stripe account identifier
    const updatedIdentifiers = identifier.filter((_, index) => index !== stripeAccountIndex);
    const updatedExtensions = this.resource.extension?.filter(
      (extension) => extension.url !== Practice.Extension.StripePaymentSetupStatus,
    );

    // Update the Organization resource with the modified identifier array
    const updatedOrganization = await medplum.updateResource({
      ...this.resource,
      identifier: updatedIdentifiers,
      extension: updatedExtensions,
    });

    // Return a new Practice instance based on the updated resource
    return new Practice(updatedOrganization);
  }

  /**
   * Set or remove the Stripe account ID for the practice
   *
   * @param medplum - The Medplum client
   * @param stripeAccountId - The Stripe account ID to set, or null to remove
   * @returns The updated practice
   */
  async setStripeAccountId(
    medplum: MedplumClient,
    stripeAccountId: string | null,
  ): Promise<Practice> {
    if (stripeAccountId === null) {
      return this.removeStripeAccountId(medplum);
    }

    const patchedOrganization = await Practice.patchResource(medplum, {
      id: this.id,
      patch: {
        identifier: [
          {
            system: STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS,
            value: stripeAccountId,
          },
        ],
      },
    });

    return new Practice(patchedOrganization);
  }

  //------------------------------------------------------------------------------------------------
  //                                      CLASS METHODS
  //------------------------------------------------------------------------------------------------
  /**
   * Get a practice by its ID.
   *
   * @param medplum - The Medplum client.
   * @param id - The ID of the practice.
   * @returns The practice.
   * @throws Error if the practice is not found.
   */
  static async getById(medplum: MedplumClient, id: string): Promise<Practice> {
    try {
      const organization = await medplum.readResource("Organization", id);
      return new Practice(organization);
    } catch (error) {
      if (!isNotFoundError(error)) {
        throw error;
      }

      throw new Error("Practice not found");
    }
  }

  static async getByReference(medplum: MedplumClient, reference: string): Promise<Practice> {
    const id = resolveId({ reference });
    if (!id) throw new Error("Invalid practice reference");

    const organization = await medplum.readResource("Organization", id);
    if (!organization) throw new Error("Practice not found");
    return new Practice(organization);
  }

  /**
   * Search for a practice by its slug
   *
   * @param medplum - The Medplum client
   * @param identifier.system - The system of the identifier
   * @param identifier.value - The value of the identifier
   * @returns The practice or undefined if not found
   */
  static async getByIdentifier(
    medplum: MedplumClient,
    identifier: {
      system: string;
      value: string;
    },
  ): Promise<Practice | undefined> {
    const organization = await medplum.searchOne(Practice.ResourceType, {
      identifier: `${identifier.system}|${identifier.value}`,
    });
    return organization ? new Practice(organization) : undefined;
  }

  /**
   * Get the practice to which the patient belongs.
   *
   * @param medplum - The Medplum client.
   * @param query.patientRef - The reference to the patient.
   * @returns The practice for the patient.
   */
  static async getForPatient(
    medplum: MedplumClient,
    query: { patientRef: string },
  ): Promise<Practice> {
    const resources: Array<Account | Organization> = await medplum.searchResources("Account", {
      status: MemberAccount.Status.Active,
      subject: query.patientRef,
      _include: "Account:owner",
    });

    const practiceResource = resources.find((resource) => resource.resourceType === "Organization");
    if (!practiceResource) throw new Error("Patient not found");

    return new Practice(practiceResource);
  }

  /**
   * Get the practice that the provider belongs to.
   *
   * @param medplum - The Medplum client.
   * @param query.providerRef - The reference to the provider.
   * @returns The practice for the provider.
   */
  static async getForProvider(
    medplum: MedplumClient,
    query: { provider: Reference<Practitioner> },
  ): Promise<Practice | undefined> {
    const resources: Array<PractitionerRole | Organization> = await medplum.searchResources(
      "PractitionerRole",
      {
        active: true,
        practitioner: query.provider.reference,
        _include: "PractitionerRole:organization",
        _count: 1,
      },
    );

    const practiceResource = resources.find(
      (resource) => resource.resourceType === Practice.ResourceType,
    );
    return practiceResource ? new Practice(practiceResource) : undefined;
  }

  static async fromReference(
    medplum: MedplumClient,
    reference: Reference<Organization>,
  ): Promise<Practice> {
    try {
      const organization = await medplum.readReference<Organization>(reference);
      return new Practice(organization);
    } catch (_error) {
      throw new Error("Practice not found");
    }
  }

  static async searchOneByVitalTeamId(
    medplum: MedplumClient,
    vitalTeamId: string,
  ): Promise<Practice | undefined> {
    const organization = await medplum.searchOne("Organization", {
      identifier: `${VITAL_IDENTIFIER_SYSTEM_TEAMS}|${vitalTeamId}`,
    });
    return organization ? new Practice(organization) : undefined;
  }

  /**
   * Find a practice by its Photon org id.
   *
   * @param medplum - The Medplum client.
   * @param photonOrgId - The Photon org id.
   * @returns The practice or undefined if not found.
   */
  static async searchOneByPhotonOrgId(
    medplum: MedplumClient,
    photonOrgId: string,
  ): Promise<Practice | undefined> {
    return await Practice.getByIdentifier(medplum, {
      system: PHOTON_ORGS,
      value: photonOrgId,
    });
  }

  /**
   * Search for a practice by its fax number.
   *
   * Matches both the E.164 form and the bare US 10-digit form. Every write path stores the fax
   * identifier as E.164, but legacy practices still hold a 10-digit value, and an E.164-only token
   * never matches those. Inbound fax routing depends on this lookup, so a miss discards the fax.
   *
   * @param medplum - The Medplum client.
   * @param query - The query.
   * @returns The practice or undefined if not found.
   */
  static async searchOne(
    medplum: MedplumClient,
    query: { fax: string },
  ): Promise<Practice | undefined> {
    const identifiers: string[] = [];

    if (query.fax) {
      const normalizedFax = toE164PhoneNumber(query.fax);
      if (!normalizedFax) {
        return undefined;
      }
      identifiers.push(`${Practice.Identifier.Fax}|${normalizedFax}`);

      // Legacy 10-digit fallback — same reason the inbound sender lookup tries both forms.
      const legacyUsFax = normalizeUSPhoneNumber(normalizedFax);
      if (legacyUsFax) {
        identifiers.push(`${Practice.Identifier.Fax}|${legacyUsFax}`);
      }
    }

    const practice = await Practice.searchOneResource(medplum, {
      identifier: identifiers.length > 0 ? identifiers.join(",") : undefined,
    });

    return practice ? new Practice(practice) : undefined;
  }

  /**
   * Create a practice.
   *
   * @param medplum - The Medplum client.
   * @param request - The request.
   * @returns The practice.
   */
  static async create(
    medplum: MedplumClient,
    request: CreatePracticeRequest,
    options?: CreateResourceOptions,
  ): Promise<Practice> {
    // If there is already a practice with the same slug, return it.
    const existingPractice = await searchOnePractice(medplum, { slug: request.slug });
    if (existingPractice) return existingPractice;

    const phoneString = request.phoneExt ? `${request.phone},${request.phoneExt}` : request.phone;
    const organization = await Practice.createResource(
      medplum,
      {
        partOf: { reference: `Organization/${getVibrantSuperOrgId()}` },
        name: request.name,
        identifier: pruneSystemValuePairs([
          { system: "slug", value: request.slug },
          {
            system: Practice.Identifier.Fax,
            value: request.fax ? toE164PhoneNumber(request.fax) : undefined,
          },
          {
            system: Practice.Identifier.TaxId,
            value: request.taxId,
          },
        ]),
        address: [
          {
            line: [request.addressLine1, request.addressLine2].filter(Boolean) as string[],
            city: request.city,
            postalCode: request.zipCode,
            state: request.stateTwoLetter,
          },
        ],
        contact: [
          {
            name: { text: request.adminName },
            telecom: [{ system: "email", value: request.adminEmail }],
            purpose: { coding: [{ code: "admin" }] },
          },
        ],
        telecom: pruneSystemValuePairs([
          { system: "phone", value: phoneString },
          { system: "fax", value: request.fax ? toE164PhoneNumber(request.fax) : undefined },
        ]),
        extension: [
          {
            url: Provider.Identifier.NPI,
            valueString: request.adminNpi,
          },
          ...(request.labPartnerInterfaces
            ? Object.entries(request.labPartnerInterfaces).map(([partnerSlug, isInterface]) => ({
                url: `vibrant.practice.lab-partner.${partnerSlug}.is-interface`,
                valueBoolean: isInterface,
              }))
            : []),
        ],
      },
      options,
    );
    return new Practice(organization);
  }

  /**
   * Returns the number of members in the practice.
   *
   * @param medplum - The Medplum client.
   * @param practiceId - The ID of the practice.
   * @returns The number of members in the practice.
   */
  static async getMemberCount(medplum: MedplumClient, practiceId: string): Promise<number> {
    return await MemberAccount.getCount(medplum, {
      owner: `Organization/${practiceId}`,
    });
  }

  /**
   * Updates a practice.
   *
   * @param medplum - The Medplum client.
   * @param practiceId - The ID of the practice.
   * @param data - The data to update the practice with.
   * @returns The updated practice.
   */
  static async update(
    medplum: MedplumClient,
    practiceId: string,
    data: UpdatePracticeData,
  ): Promise<Practice> {
    let adminContact: OrganizationContact | undefined;

    if (data.adminEmail && data.adminName) {
      adminContact = {
        name: { text: data.adminName },
        telecom: [{ system: "email", value: data.adminEmail }],
        purpose: { coding: [{ code: "admin" }] },
      };
    }

    const resource = await Practice.patchResource(medplum, {
      id: practiceId,
      patch: {
        name: data.name,
        contact: adminContact ? [adminContact] : undefined,
        address: data.addressLine1
          ? {
              line: [data.addressLine1, data.addressLine2].filter(Boolean) as string[],
              city: data.city,
              postalCode: data.zipCode,
              state: data.stateTwoLetter,
            }
          : undefined,
        identifier: pruneSystemValuePairs([
          {
            system: Practice.Identifier.Fax,
            value: data.fax ? toE164PhoneNumber(data.fax) : undefined,
          },
        ]),
        telecom: pruneSystemValuePairs([
          {
            system: "phone",
            value: data.phoneExt ? `${data.phone},${data.phoneExt}` : data.phone,
          },
          {
            system: "fax",
            value: data.fax ? toE164PhoneNumber(data.fax) : undefined,
          },
        ]),
      },
    });

    return new Practice(resource);
  }

  /**
   * Creates a Cal.com team for the practice if one doesn't exist.
   * This enables the practice to use Calendar V2 features.
   *
   * @param medplum - The Medplum client
   * @param calTeamClient - The Cal.com team client
   * @param practiceId - The ID of the practice
   * @returns The practice with the Cal.com team ID
   */
  static async ensureCalTeamSetup(
    medplum: MedplumClient,
    calTeamClient: CalTeamClient,
    practiceId: string,
  ): Promise<{
    practice: Practice;
    calTeamId: string;
  }> {
    const practice = await Practice.getById(medplum, practiceId);

    // If practice already has a Cal.com team, return it
    if (practice.calTeamId) {
      return {
        practice,
        calTeamId: practice.calTeamId,
      };
    }

    // Re-check before creating to prevent race condition
    const latestPractice = await Practice.getById(medplum, practiceId);
    if (latestPractice.calTeamId) {
      return {
        practice: latestPractice,
        calTeamId: latestPractice.calTeamId,
      };
    }

    // Create the Cal.com team
    const teamResponse = await calTeamClient.createTeam({
      name: practice.name,
      slug: practice.slug,
      isPrivate: false,
      autoAcceptCreator: true,
    });

    if (teamResponse.status !== "success" || !teamResponse.data) {
      throw new Error("Failed to create Cal.com team");
    }

    const calTeamId = teamResponse.data.id.toString();

    // Update practice with team ID
    const updatedPractice = await patchPractice(medplum, practiceId, { calTeamId });

    logger.info(`Created Cal.com team for practice ${practice.slug}`, {
      practiceId,
      calTeamId,
    });

    return { practice: updatedPractice, calTeamId };
  }
}

/**
 * Get a practice by its slug
 *
 * @param medplum - The Medplum client
 * @param slug - The slug of the practice
 * @returns The practice or undefined if not found
 */
export async function getPracticeBySlug(
  medplum: MedplumClient,
  slug: string,
): Promise<Practice | undefined> {
  const organization = await medplum.searchOne("Organization", { identifier: `slug|${slug}` });
  return organization ? new Practice(organization) : undefined;
}

export async function getPractice(medplum: MedplumClient, practiceId: string): Promise<Practice> {
  try {
    const organization = await medplum.readResource("Organization", practiceId);
    return new Practice(organization);
  } catch (_error) {
    throw new Error("Practice not found");
  }
}

export async function searchOnePractice(
  medplum: MedplumClient,
  searchQuery: PracticeSearchQuery,
): Promise<Practice | undefined> {
  // if searchquery has no keys, return undefined
  if (Object.keys(searchQuery).length === 0) {
    return undefined;
  }

  const queryTypes: QueryTypes = {};

  // TODO what to do for queries that contain multiple keys? identifier gets overwritten
  if (searchQuery.slug) {
    queryTypes.identifier = `slug|${searchQuery.slug}`;
  }
  if (searchQuery.stripeAccountId) {
    queryTypes.identifier = `${STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS}|${searchQuery.stripeAccountId}`;
  }
  if (searchQuery.medmoLocationId) {
    queryTypes.identifier = `${MEDMO_IDENTIFIER_SYSTEM_LOCATIONS}|${searchQuery.medmoLocationId}`;
  }
  const organization = await medplum.searchOne("Organization", queryTypes);
  return organization ? new Practice(organization) : undefined;
}

export async function patchPractice(
  medplum: MedplumClient,
  practiceId: string,
  patchQuery: PracticePatchQuery,
): Promise<Practice> {
  const organization = await medplum.readResource("Organization", practiceId);

  if (!organization) {
    throw new Error("Practice not found");
  }

  const patchOperations: PatchOperation[] = [];

  if (patchQuery.name) {
    patchOperations.push({
      op: "replace",
      path: "/name",
      value: patchQuery.name,
    });
  }

  if (patchQuery.adminContact) {
    let contactPatchOperation: PatchOperation;

    if (!organization.contact) {
      contactPatchOperation = {
        op: "add",
        path: "/contact",
        value: [
          {
            name: {
              text: patchQuery.adminContact.adminName,
            },
            telecom: [{ system: "email", value: patchQuery.adminContact.adminEmail }],
            purpose: {
              coding: [{ code: "admin" }],
            },
          },
        ],
      };
    } else {
      const adminContactIndex =
        organization.contact?.findIndex((contact) =>
          contact.purpose?.coding?.some((coding) => coding.code === "admin"),
        ) ?? -1;

      if (adminContactIndex !== -1) {
        contactPatchOperation = {
          op: "replace",
          path: `/contact/${adminContactIndex}`,
          value: {
            name: {
              text: patchQuery.adminContact.adminName,
            },
            telecom: [{ system: "email", value: patchQuery.adminContact.adminEmail }],
            purpose: {
              coding: [{ code: "admin" }],
            },
          },
        };
      } else {
        contactPatchOperation = {
          op: "add",
          path: "/contact/-",
          value: {
            name: {
              text: patchQuery.adminContact.adminName,
            },
            telecom: [{ system: "email", value: patchQuery.adminContact.adminEmail }],
            purpose: {
              coding: [{ code: "admin" }],
            },
          },
        };
      }
    }
    patchOperations.push(contactPatchOperation);
  }

  const identifierPatchRequests: IdentifierPatchRequest[] = [];
  const extensionPatchRequests: ExtensionPatchRequest[] = [];

  if (patchQuery.patientAgreementTemplateId !== undefined) {
    const templateId = patchQuery.patientAgreementTemplateId?.trim();
    const hasTemplateId = !!templateId?.length;
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.PatientAgreementTemplateId,
      value: hasTemplateId ? templateId : null,
    });

    // The first template is the practice opting back in to agreements, so retire the skip flag
    // it set during practice setup. Nothing else clears that flag, and it wins in
    // `shouldRequirePatientAgreements` while `sendPatientAgreementsIfNeeded` ignores it — leave
    // both set and we mail a consent the patient flow never asks anyone to sign.
    // Only on unset -> set: re-saving a practice that deliberately skipped must not switch
    // agreements back on. An explicit skipPatientAgreements in the same patch still wins.
    const hadTemplateId = !!findExtension(
      organization,
      Practice.Extension.PatientAgreementTemplateId,
    )?.valueString?.length;
    if (hasTemplateId && !hadTemplateId && patchQuery.skipPatientAgreements === undefined) {
      extensionPatchRequests.push({
        resource: organization,
        url: Practice.Extension.SkipPatientAgreements,
        value: false,
      });
    }

    // Record the first template only. Patients invited before this instant never got an agreement,
    // so it bounds the backfill prompt. A re-save, a replacement, or a clear-then-set keeps it.
    const hasTemplateSetAt = !!findExtension(
      organization,
      Practice.Extension.PatientAgreementTemplateSetAt,
    );
    if (hasTemplateId && !hadTemplateId && !hasTemplateSetAt) {
      extensionPatchRequests.push({
        resource: organization,
        url: Practice.Extension.PatientAgreementTemplateSetAt,
        value: new Date().toISOString(),
      });
    }
  }

  if (patchQuery.skipPatientAgreements !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.SkipPatientAgreements,
      value: patchQuery.skipPatientAgreements,
    });
  }

  if (patchQuery.skipPaymentSetup !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.SkipPaymentSetup,
      value: patchQuery.skipPaymentSetup,
    });
  }

  if (patchQuery.skipTeamInvites !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.CheckOffTeamInvites,
      value: patchQuery.skipTeamInvites,
    });
  }

  if (patchQuery.sendPatientReactivationEmails !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.SendPatientReactivationEmails,
      value: patchQuery.sendPatientReactivationEmails,
    });
  }

  if (patchQuery.sendPatientEmails !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.SendPatientEmails,
      value: patchQuery.sendPatientEmails,
    });
  }

  if (patchQuery.emailFooterMarkdown !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.EmailFooterMarkdown,
      value: patchQuery.emailFooterMarkdown,
    });
  }

  if (patchQuery.socialLinks !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.SocialLinks,
      // Normalize and validate here, so every reader gets a link an email client can follow.
      value: JSON.stringify(
        practiceSocialLinksSchema.parse(buildPracticeSocialLinks(patchQuery.socialLinks)),
      ),
    });
  }

  if (patchQuery.stripeAccountId) {
    identifierPatchRequests.push({
      resource: organization,
      system: STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS,
      value: patchQuery.stripeAccountId,
    });
  }

  if (patchQuery.stripePaymentSetupStatus !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.StripePaymentSetupStatus,
      value: patchQuery.stripePaymentSetupStatus,
    });
  }

  if (patchQuery.stripeCustomerId) {
    identifierPatchRequests.push({
      resource: organization,
      system: STRIPE_IDENTIFIER_SYSTEM_CUSTOMERS,
      value: patchQuery.stripeCustomerId,
    });
  }

  if (patchQuery.medmoLocationId) {
    identifierPatchRequests.push({
      resource: organization,
      system: MEDMO_IDENTIFIER_SYSTEM_LOCATIONS,
      value: patchQuery.medmoLocationId.toString(),
    });
  }

  if (patchQuery.timezone !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.Timezone,
      value: patchQuery.timezone,
    });
  }

  if (patchQuery.gtmContainerId !== undefined) {
    extensionPatchRequests.push({
      resource: organization,
      url: Practice.Extension.GtmContainerId,
      value: patchQuery.gtmContainerId,
    });
  }

  if (patchQuery.labPartnerInterfaces) {
    Object.entries(patchQuery.labPartnerInterfaces).forEach(([partnerSlug, isInterface]) => {
      extensionPatchRequests.push({
        resource: organization,
        url: `vibrant.practice.lab-partner.${partnerSlug}.is-interface`,
        value: isInterface,
      });
    });
  }

  if (patchQuery.address) {
    const address: Address = toFhirPostalAddress(patchQuery.address);

    if (!organization.address) {
      patchOperations.push({
        op: "add",
        path: "/address",
        value: [address],
      });
    } else if (organization.address.length === 0) {
      patchOperations.push({
        op: "add",
        path: "/address/-",
        value: address,
      });
    } else {
      patchOperations.push({
        op: "replace",
        path: "/address/0",
        value: address,
      });
    }
  }

  // Handle fax updates and clearing
  let fax: string | undefined;
  let shouldClearFax = false;

  if (patchQuery.fax !== undefined) {
    if (patchQuery.fax.trim() === "") {
      // Explicitly clear fax if empty string is provided
      shouldClearFax = true;
    } else {
      // Convert to E.164 format
      fax = toE164PhoneNumber(patchQuery.fax);
      if (!fax) {
        throw new Error("Invalid fax number format");
      }
    }
  }

  if (shouldClearFax) {
    identifierPatchRequests.push({
      resource: organization,
      system: Practice.Identifier.Fax,
      value: null,
    });
  } else if (fax) {
    // Update fax identifier
    identifierPatchRequests.push({
      resource: organization,
      system: Practice.Identifier.Fax,
      value: fax,
    });
  }

  if (patchQuery.taxId !== undefined) {
    identifierPatchRequests.push({
      resource: organization,
      system: Practice.Identifier.TaxId,
      value: patchQuery.taxId,
    });
  }

  const shouldUpdateTelecom =
    patchQuery.phone !== undefined ||
    patchQuery.phoneExt !== undefined ||
    patchQuery.fax !== undefined;

  if (shouldUpdateTelecom) {
    const originalTelecomSnapshot: ContactPoint[] = (organization.telecom ?? []).map((telecom) => ({
      ...telecom,
    }));
    let updatedTelecomEntries: ContactPoint[] = originalTelecomSnapshot.map((telecom) => ({
      ...telecom,
    }));

    if (shouldClearFax) {
      updatedTelecomEntries = updatedTelecomEntries.filter((telecom) => telecom.system !== "fax");
    }

    if (fax) {
      const faxEntry: ContactPoint = { system: "fax", value: fax };
      const faxIndex = updatedTelecomEntries.findIndex((telecom) => telecom.system === "fax");

      if (faxIndex !== -1) {
        updatedTelecomEntries[faxIndex] = faxEntry;
      } else {
        updatedTelecomEntries.push(faxEntry);
      }
    }

    if (patchQuery.phone) {
      const phoneString = patchQuery.phoneExt
        ? `${patchQuery.phone},${patchQuery.phoneExt}`
        : patchQuery.phone;
      const phoneEntry: ContactPoint = { system: "phone", value: phoneString };
      const phoneIndex = updatedTelecomEntries.findIndex((telecom) => telecom.system === "phone");

      if (phoneIndex !== -1) {
        updatedTelecomEntries[phoneIndex] = phoneEntry;
      } else {
        updatedTelecomEntries.push(phoneEntry);
      }
    }

    const telecomHasChanged =
      JSON.stringify(updatedTelecomEntries) !== JSON.stringify(originalTelecomSnapshot);

    if (telecomHasChanged) {
      if (organization.telecom) {
        patchOperations.push({
          op: "replace",
          path: "/telecom",
          value: updatedTelecomEntries,
        });
      } else if (updatedTelecomEntries.length > 0) {
        patchOperations.push({
          op: "add",
          path: "/telecom",
          value: updatedTelecomEntries,
        });
      }
    }
  }

  if (patchQuery.vitalTeamId) {
    identifierPatchRequests.push({
      resource: organization,
      system: VITAL_IDENTIFIER_SYSTEM_TEAMS,
      value: patchQuery.vitalTeamId,
    });
  }

  if (patchQuery.vitalWebhookId) {
    identifierPatchRequests.push({
      resource: organization,
      system: VITAL_IDENTIFIER_SYSTEM_WEBHOOK_IDS,
      value: patchQuery.vitalWebhookId,
    });
  }

  if (patchQuery.photonOrgId) {
    identifierPatchRequests.push({
      resource: organization,
      system: PHOTON_ORGS,
      value: patchQuery.photonOrgId,
    });
  }

  if (patchQuery.photonFrontendClientId) {
    identifierPatchRequests.push({
      resource: organization,
      system: PHOTON_FRONTEND_CLIENT_IDS,
      value: patchQuery.photonFrontendClientId,
    });
  }

  if (patchQuery.photonBackendClientId) {
    identifierPatchRequests.push({
      resource: organization,
      system: PHOTON_BACKEND_CLIENT_IDS,
      value: patchQuery.photonBackendClientId,
    });
  }

  if (patchQuery.calTeamId) {
    identifierPatchRequests.push({
      resource: organization,
      system: CAL_IDENTIFIER_SYSTEM_TEAMS,
      value: patchQuery.calTeamId,
    });
  }

  const patchedOrganization = await medplum.patchResource("Organization", practiceId, [
    ...patchOperations,
    ...getIdentifierPatchOperations(organization, identifierPatchRequests),
    ...getExtensionPatchOperations(organization, extensionPatchRequests),
  ]);
  return new Practice(patchedOrganization);
}

/**
 * Marks the agreement backfill prompt answered for `templateId`. Patches only that one extension,
 * so a caller with narrow intent can't touch other Organization fields through it.
 */
export async function recordPatientAgreementBackfillResolved(
  medplum: MedplumClient,
  { practiceId, templateId }: { practiceId: string; templateId: string },
): Promise<Practice> {
  const organization = await medplum.readResource("Organization", practiceId);
  const patchedOrganization = await medplum.patchResource(
    "Organization",
    practiceId,
    getExtensionPatchOperations(organization, [
      {
        resource: organization,
        url: Practice.Extension.PatientAgreementBackfillResolvedTemplateId,
        value: templateId,
      },
    ]),
  );
  return new Practice(patchedOrganization);
}

async function ensurePendingServicePurchases({
  medplum,
  memberAccount,
  practiceId,
  serviceIds,
}: {
  medplum: MedplumClient;
  memberAccount: MemberAccount;
  practiceId: string;
  serviceIds: string[];
}): Promise<void> {
  const accountRef = memberAccount.referenceObject;
  if (!accountRef) {
    logger.warn("Cannot create purchases for service choices without account reference", {
      memberAccountId: memberAccount.id,
    });
    return;
  }

  const uniqueServiceIds = Array.from(new Set(serviceIds));
  const failureMessages: Array<{ serviceId: string; message: string }> = [];

  for (const serviceId of uniqueServiceIds) {
    if (!serviceId) continue;

    try {
      const service = await getService(medplum, serviceId);
      const providedByRef = service.healthcareService.providedBy;
      const servicePracticeId = providedByRef ? resolveId(providedByRef) : undefined;

      if (servicePracticeId && servicePracticeId !== practiceId) {
        logger.warn("Skipping purchase creation for service outside practice", {
          serviceId,
          servicePracticeId,
          practiceId,
        });
        continue;
      }

      const serviceRef = {
        reference: `HealthcareService/${serviceId}`,
        display: service.name,
      };

      const existingPurchases = await Purchase.search(medplum, {
        account: accountRef,
        service: serviceRef,
      });

      if (existingPurchases.length > 1) {
        const purchaseIds = existingPurchases.map((purchase) => purchase.id);
        logger.error("Multiple service purchases found for account/service", {
          memberAccountId: memberAccount.id,
          serviceId,
          practiceId,
          purchaseIds,
        });
        throw new Error(
          `Multiple service purchases found for memberAccount ${memberAccount.id} and service ${serviceId}`,
        );
      }

      const existingPurchase = existingPurchases[0];
      if (existingPurchase) {
        if (existingPurchase.status === Purchase.Status.NotBillable) {
          logger.info("Reopening not-billable service purchase for required service invite", {
            purchaseId: existingPurchase.id,
            accountId: memberAccount.id,
            serviceId,
            practiceId,
          });
          await Purchase.update(medplum, existingPurchase.id, { status: Purchase.Status.Unpaid });
        }
        continue;
      }

      await Purchase.create(
        medplum,
        {
          account: accountRef,
          service: serviceRef,
          status: Purchase.Status.Unpaid,
        },
        {
          ifNoneExist: [
            `account=${accountRef.reference}`,
            `code=${Purchase.Coding.System.Services}|${serviceId}`,
          ].join("&"),
        },
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error("Failed to ensure pending purchase for service choice", {
        memberAccountId: memberAccount.id,
        serviceId,
        error: errorMessage,
      });
      failureMessages.push({ serviceId, message: errorMessage });
    }
  }

  if (failureMessages.length > 0) {
    const summary = failureMessages
      .map(({ serviceId, message }) => `${serviceId} (${message})`)
      .join("; ");
    throw new Error(
      `Failed to ensure pending purchases for memberAccount ${memberAccount.id}: ${summary}`,
    );
  }
}

async function markUnpaidServicePurchasesNotBillable({
  medplum,
  memberAccount,
  practiceId,
}: {
  medplum: MedplumClient;
  memberAccount: MemberAccount;
  practiceId: string;
}): Promise<void> {
  const purchases = await Purchase.search(medplum, {
    account: memberAccount.referenceObject,
  });
  const unpaidServicePurchases = purchases.filter((purchase) => {
    return Boolean(purchase.serviceId) && purchase.status === Purchase.Status.Unpaid;
  });

  if (unpaidServicePurchases.length === 0) {
    return;
  }

  logger.info("Marking unpaid service purchases not billable for no-payment invite", {
    memberAccountId: memberAccount.id,
    memberId: memberAccount.memberId,
    practiceId,
    purchaseIds: unpaidServicePurchases.map((purchase) => purchase.id),
  });

  for (const purchase of unpaidServicePurchases) {
    await Purchase.update(medplum, purchase.id, { status: Purchase.Status.NotBillable });
  }
}
